'use client';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { Popover, usePopoverDescendantContainment } from '../../base/popover';
import { ActionMenu } from './ActionMenu';

type OpenInvocation = {
    id: number;
    position: {
        x: number;
        y: number;
    };
};

export type CursorAnchoredMenuProps = {
    children: React.ReactNode;
    menu: React.ReactNode;
    className?: string;
    popoverClassName?: string;
    isDisabled?: boolean;
    dismissWhen?: boolean;
    shouldOpen?: (event: React.MouseEvent<HTMLDivElement>) => boolean;
    onOpenChange?: (isOpen: boolean) => void;
};

const POPOVER_SELECTOR = '[data-cursor-anchored-menu-popover]';

type ContainsNode = (node: Node) => boolean;

/** Checks whether an input event started inside this context-menu surface. */
const isInternalMenuEvent = (event: Event, containsOwnedPopoverNode: ContainsNode | null): boolean => {
    const target = event.target;
    if (!(target instanceof Element)) return false;
    if (target.closest(POPOVER_SELECTOR) !== null) return true;

    // React Aria portals this menu's submenu popovers outside the cursor-anchored popover, so ask the
    // popover group whether the target lives in one of them.
    return !!containsOwnedPopoverNode?.(target);
};

/** Lifts this invocation's popover-tree containment out to the menu's document-level listeners. */
const CursorAnchoredMenuContainmentProbe: React.FC<{
    containmentRef: MutableRefObject<ContainsNode | null>;
}> = ({ containmentRef }) => {
    const containsDescendantPopoverNode = usePopoverDescendantContainment();

    useLayoutEffect(() => {
        containmentRef.current = containsDescendantPopoverNode;

        return () => {
            containmentRef.current = null;
        };
    }, [containmentRef, containsDescendantPopoverNode]);

    return null;
};

type CursorAnchoredMenuPopoverProps = {
    invocation: OpenInvocation;
    menu: React.ReactNode;
    popoverClassName?: string;
    close: (invocationId: number) => void;
    containmentRef: MutableRefObject<ContainsNode | null>;
    triggerAreaRef: React.RefObject<HTMLDivElement | null>;
};

/** Activates the hovered item when a held secondary mouse button is released. */
const activateMenuItemOnSecondaryMouseUp = (event: React.MouseEvent): void => {
    if (event.button !== 2 || !(event.target instanceof Element)) return;

    const menuItem = event.target.closest<HTMLElement>('[role^="menuitem"]');
    if (!menuItem || menuItem.getAttribute('aria-disabled') === 'true') return;

    // React Aria only bridges primary-button release, so use its click path for action and dismissal semantics.
    menuItem.click();
};

/** Prevents a context menu opened inside this portal from reaching an underlying menu route. */
const preventNestedActionMenu = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
};

/** Renders one keyed cursor anchor and its controlled Roma menu popover. */
const CursorAnchoredMenuPopover: React.FC<CursorAnchoredMenuPopoverProps> = ({
    invocation,
    menu,
    popoverClassName,
    close,
    containmentRef,
    triggerAreaRef,
}) => {
    useEffect(() => {
        const closeOnResize = () => close(invocation.id);

        window.addEventListener('resize', closeOnResize);

        return () => window.removeEventListener('resize', closeOnResize);
    }, [close, invocation.id]);

    // Positions the popover at the invocation's cursor point; the trigger-area element only serves
    // as React Aria's required target element, its bounding box is overridden by this rect.
    const getTargetRect = () => new DOMRect(invocation.position.x, invocation.position.y, 0, 0);

    return createPortal(
        <Popover.MenuTrigger
            isOpen
            onOpenChange={(isOpen) => {
                if (!isOpen) close(invocation.id);
            }}
        >
            <Popover
                triggerRef={triggerAreaRef}
                getTargetRect={getTargetRect}
                placement="bottom start"
                offset={0}
                containerPadding={8}
                popoverClassName={popoverClassName}
                data-cursor-anchored-menu-popover
            >
                <div
                    className="contents"
                    onContextMenu={preventNestedActionMenu}
                    onMouseUp={activateMenuItemOnSecondaryMouseUp}
                    onKeyDown={(event) => {
                        event.stopPropagation();
                        event.nativeEvent.stopImmediatePropagation();
                    }}
                >
                    <CursorAnchoredMenuContainmentProbe containmentRef={containmentRef} />
                    {menu}
                </div>
            </Popover>
        </Popover.MenuTrigger>,
        document.body,
    );
};

/** Opens a Roma menu at a consumed context-menu event while preserving disabled native-menu behaviour. */
export const CursorAnchoredMenu: React.FC<CursorAnchoredMenuProps> = ({
    children,
    menu,
    className,
    popoverClassName = ActionMenu.styles.width,
    isDisabled = false,
    dismissWhen = false,
    shouldOpen,
    onOpenChange,
}) => {
    const nextInvocationIdRef = useRef(0);
    const [openInvocation, setOpenInvocation] = useState<OpenInvocation | null>(null);
    const ownedPopoverContainmentRef = useRef<ContainsNode | null>(null);
    const triggerAreaRef = useRef<HTMLDivElement>(null);

    const close = useCallback((invocationId: number) => {
        setOpenInvocation((current) => (current?.id === invocationId ? null : current));
    }, []);

    useEffect(() => {
        if (!dismissWhen && !isDisabled) return;
        setOpenInvocation(null);
    }, [dismissWhen, isDisabled]);

    const isOpen = openInvocation !== null;
    useLayoutEffect(() => {
        if (!isOpen) return;

        // React Aria closes positioned popovers when an ancestor scrolls. Keep this cursor-anchored menu fixed instead.
        // This pre-empts React Aria's own window capture scroll listener only because this parent
        // useLayoutEffect registers first, before the popover child's useEffect (useCloseOnScroll).
        // That ordering holds while both effects depend only on the open state — re-registering this
        // listener mid-open (extra deps here) would move it behind React Aria's and break keep-open.
        const preserveOpenOnScroll = (event: Event) => event.stopImmediatePropagation();
        const preventBackgroundScroll = (event: Event) => {
            if (!isInternalMenuEvent(event, ownedPopoverContainmentRef.current)) event.preventDefault();
        };

        window.addEventListener('scroll', preserveOpenOnScroll, { capture: true, passive: true });
        window.addEventListener('wheel', preventBackgroundScroll, { capture: true, passive: false });
        window.addEventListener('touchmove', preventBackgroundScroll, { capture: true, passive: false });

        return () => {
            window.removeEventListener('scroll', preserveOpenOnScroll, { capture: true });
            window.removeEventListener('wheel', preventBackgroundScroll, { capture: true });
            window.removeEventListener('touchmove', preventBackgroundScroll, { capture: true });
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const closeOnPointerDown = (event: PointerEvent) => {
            if (!isInternalMenuEvent(event, ownedPopoverContainmentRef.current)) setOpenInvocation(null);
        };

        document.addEventListener('pointerdown', closeOnPointerDown, true);
        return () => document.removeEventListener('pointerdown', closeOnPointerDown, true);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        onOpenChange?.(true);
        return () => onOpenChange?.(false);
    }, [isOpen, onOpenChange]);

    const onActionMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as Node;

        // Portal events follow the React tree, so only accept events physically inside the trigger area.
        if (!event.currentTarget.contains(target)) return;

        const acceptsOpen = !isDisabled && (shouldOpen ? shouldOpen(event) : true);
        if (!acceptsOpen) return;

        event.preventDefault();
        event.stopPropagation();

        nextInvocationIdRef.current += 1;
        setOpenInvocation({
            id: nextInvocationIdRef.current,
            position: { x: event.clientX, y: event.clientY },
        });
    };

    return (
        <div ref={triggerAreaRef} className={className} onContextMenu={onActionMenu}>
            {children}
            {openInvocation && (
                <CursorAnchoredMenuPopover
                    key={openInvocation.id}
                    invocation={openInvocation}
                    menu={menu}
                    popoverClassName={popoverClassName}
                    close={close}
                    containmentRef={ownedPopoverContainmentRef}
                    triggerAreaRef={triggerAreaRef}
                />
            )}
        </div>
    );
};
